import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
} from "@repo/ui";
import { Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { generateInitials } from "@/hooks/use-generate-initials";
import { createFirstUser } from "@/lib/api/auth";
import type { ApiClientError } from "@/lib/api/client";

interface CreateFirstUserModalProps {
  open: boolean;
  pharmacyName: string;
  defaultFirstName?: string;
  defaultLastName?: string;
}

export function CreateFirstUserModal({
  open,
  pharmacyName,
  defaultFirstName = "",
  defaultLastName = "",
}: CreateFirstUserModalProps) {
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [initials, setInitials] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Auto-generate initials when name changes
  useEffect(() => {
    if (firstName && lastName && !initials) {
      const generated = generateInitials(firstName, lastName);
      if (generated) {
        setInitials(generated);
      }
    }
  }, [firstName, lastName, initials]);

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    const upperInitials = initials.toUpperCase();
    if (!upperInitials || upperInitials.length < 2) {
      errors.initials = "Initials must be at least 2 characters";
    } else if (!/^[A-Z]{2,5}$/.test(upperInitials)) {
      errors.initials = "Initials must be 2-5 uppercase letters only";
    }
    if (password.length < 4 || password.length > 16) {
      errors.password = "Password must be 4-16 characters";
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields");
      return false;
    }
    return true;
  }, [firstName, lastName, initials, password, confirmPassword]);

  const handleSubmit = useCallback(async () => {
    setError("");
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await createFirstUser({
        firstName,
        lastName,
        initials: initials.toUpperCase(),
        password,
      });

      // Show success animation
      setSuccess(true);

      // Reload the page after animation to update JWT metadata
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      const message =
        (err as ApiClientError).message || "Network error. Please try again.";
      setError(message);
      console.error("Create user error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [validateForm, firstName, lastName, initials, password]);

  // Handle password input via keyDown to avoid browser password-save prompts.
  // The input value is displayed as dots purely in React state,
  // so Chrome/Safari never see a password field.
  const makeSecureKeyDown = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSubmit();
          return;
        }

        if (e.key === "Backspace") {
          e.preventDefault();
          setter((prev) => prev.slice(0, -1));
          return;
        }

        // Ignore modifier keys, function keys, navigation keys, etc.
        if (e.metaKey || e.ctrlKey || e.altKey || e.key.length > 1) {
          return;
        }

        e.preventDefault();
        setter((prev) => {
          if (prev.length >= 16) return prev;
          return prev + e.key;
        });
      },
    [handleSubmit]
  );

  const makeSecurePaste = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text");
        setter((prev) => (prev + pasted).slice(0, 16));
      },
    []
  );

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-2xl !border-0 shadow-2xl overflow-hidden !p-0 !gap-0 [&>button]:hidden !bg-transparent sm:!rounded-3xl max-h-[90vh]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AnimatePresence mode="wait">
          {!success ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="flex flex-col md:flex-row max-h-[90vh] rounded-3xl overflow-hidden"
            >
              {/* Left side - Hero section */}
              <div className="md:w-2/5 bg-gradient-to-br from-primary via-primary to-primary/90 text-white p-5 lg:p-6 relative overflow-hidden md:rounded-l-3xl flex-shrink-0">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-3xl" />

                <div className="relative h-full flex flex-col justify-between min-h-0">
                  {/* Logo/Icon */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-3"
                  >
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm">
                      <Sparkles
                        className="h-5 w-5 text-white"
                        strokeWidth={2.5}
                      />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold mb-1.5">
                        Welcome to C2 Vault
                      </h2>
                      <p className="text-white/80 text-xs leading-relaxed">
                        <span className="font-semibold text-white">
                          {pharmacyName}
                        </span>{" "}
                        is all set up and ready to go.
                      </p>
                    </div>
                  </motion.div>

                  {/* Feature highlights */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="space-y-2"
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-white mt-1.5 flex-shrink-0" />
                      <p className="text-xs text-white/90">
                        DEA-compliant tracking
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-white mt-1.5 flex-shrink-0" />
                      <p className="text-xs text-white/90">
                        Automated audit reports
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-white mt-1.5 flex-shrink-0" />
                      <p className="text-xs text-white/90">
                        Secure inventory management
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Right side - Form */}
              <div className="md:w-3/5 bg-background md:rounded-r-3xl flex flex-col overflow-hidden">
                <DialogHeader className="sr-only">
                  <DialogTitle>Create Your Admin Account</DialogTitle>
                  <DialogDescription>
                    Set up your account to access the dashboard
                  </DialogDescription>
                </DialogHeader>

                <div className="p-5 lg:p-6 overflow-y-auto flex-1">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold tracking-tight mb-1">
                        Create Your Account
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Set up your admin account to get started
                      </p>
                    </div>

                    <div className="space-y-3">
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <Alert
                            variant="destructive"
                            className="border-destructive/50 py-2"
                          >
                            <AlertDescription className="text-xs">
                              {error}
                            </AlertDescription>
                          </Alert>
                        </motion.div>
                      )}

                      <div className="grid grid-cols-2 gap-2.5">
                        <div className="space-y-1">
                          <Label
                            htmlFor="first-name-field"
                            className="text-xs font-medium"
                          >
                            First Name
                          </Label>
                          <Input
                            id="first-name-field"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={isLoading}
                            autoFocus
                            autoComplete="off"
                            className={`h-8 text-sm transition-all duration-200 ${
                              fieldErrors.firstName
                                ? "border-destructive focus-visible:ring-destructive"
                                : ""
                            }`}
                          />
                          {fieldErrors.firstName && (
                            <p className="text-xs text-destructive">
                              {fieldErrors.firstName}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <Label
                            htmlFor="last-name-field"
                            className="text-xs font-medium"
                          >
                            Last Name
                          </Label>
                          <Input
                            id="last-name-field"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            disabled={isLoading}
                            autoComplete="off"
                            className={`h-8 text-sm transition-all duration-200 ${
                              fieldErrors.lastName
                                ? "border-destructive focus-visible:ring-destructive"
                                : ""
                            }`}
                          />
                          {fieldErrors.lastName && (
                            <p className="text-xs text-destructive">
                              {fieldErrors.lastName}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label
                          htmlFor="initials-field"
                          className="text-xs font-medium"
                        >
                          Initials (Login ID)
                        </Label>
                        <Input
                          id="initials-field"
                          placeholder="JD"
                          value={initials}
                          onChange={(e) =>
                            setInitials(
                              e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z]/g, "")
                                .slice(0, 5)
                            )
                          }
                          disabled={isLoading}
                          maxLength={5}
                          autoComplete="off"
                          className={`h-8 text-sm transition-all duration-200 ${
                            fieldErrors.initials
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }`}
                        />
                        {fieldErrors.initials ? (
                          <p className="text-xs text-destructive">
                            {fieldErrors.initials}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            2-5 uppercase letters. Used to log in and identify
                            transactions.
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label
                          htmlFor="secure-pw"
                          className="text-xs font-medium"
                        >
                          Password
                        </Label>
                        <Input
                          id="secure-pw"
                          type="text"
                          placeholder="Enter your password"
                          value={"●".repeat(password.length)}
                          onKeyDown={makeSecureKeyDown(setPassword)}
                          onPaste={makeSecurePaste(setPassword)}
                          onChange={() => {}}
                          maxLength={16}
                          disabled={isLoading}
                          autoComplete="off"
                          className={`h-8 text-sm transition-all duration-200 ${
                            fieldErrors.password
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }`}
                        />
                        {fieldErrors.password && (
                          <p className="text-xs text-destructive">
                            {fieldErrors.password}
                          </p>
                        )}
                      </div>

                      <div className="space-y-1">
                        <Label
                          htmlFor="secure-pw-confirm"
                          className="text-xs font-medium"
                        >
                          Confirm Password
                        </Label>
                        <Input
                          id="secure-pw-confirm"
                          type="text"
                          placeholder="Re-enter your password"
                          value={"●".repeat(confirmPassword.length)}
                          onKeyDown={makeSecureKeyDown(setConfirmPassword)}
                          onPaste={makeSecurePaste(setConfirmPassword)}
                          onChange={() => {}}
                          maxLength={16}
                          disabled={isLoading}
                          autoComplete="off"
                          className={`h-8 text-sm transition-all duration-200 ${
                            fieldErrors.confirmPassword
                              ? "border-destructive focus-visible:ring-destructive"
                              : ""
                          }`}
                        />
                        {fieldErrors.confirmPassword && (
                          <p className="text-xs text-destructive">
                            {fieldErrors.confirmPassword}
                          </p>
                        )}
                      </div>

                      <Button
                        type="button"
                        className="w-full h-9 text-sm font-semibold mt-4"
                        disabled={isLoading}
                        onClick={handleSubmit}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating Account...
                          </>
                        ) : (
                          "Create Admin Account"
                        )}
                      </Button>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex items-center justify-center p-12 bg-gradient-to-br from-green-500 via-green-600 to-green-500 relative overflow-hidden min-h-[400px] rounded-3xl"
            >
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

              <div className="relative text-center space-y-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.2,
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                  }}
                  className="flex justify-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm">
                    <CheckCircle2
                      className="h-10 w-10 text-white"
                      strokeWidth={2.5}
                    />
                  </div>
                </motion.div>
                <div className="space-y-1.5">
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-2xl font-bold text-white tracking-tight"
                  >
                    Account Created!
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-white/90 text-sm"
                  >
                    Taking you to your dashboard...
                  </motion.p>
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="flex items-center justify-center gap-1.5"
                >
                  <div
                    className="w-2 h-2 rounded-full bg-white/60 animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 rounded-full bg-white/60 animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 rounded-full bg-white/60 animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
